import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LinearProgress } from "@mui/material";
import AppLayout from "./components/ui/AppLayout";
import CommonRoute from "./components/ui/CommonRoute";
import ProtectedRoute from "./components/ui/ProtectedRoute";
import DrawerProvider from "./provider/DrawerProvider";
import ModalProvider from "./provider/ModalProvider";
import { Toaster } from "react-hot-toast";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// Pages load on demand so the first visit only downloads what's needed.
const Home = lazy(() => import("./pages/Home"));
const PageNotFound = lazy(() => import("./pages/PageNotFound"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const WeightDiscrepancies = lazy(() => import("./pages/WeightDiscrepancies"));
const RateCalculator = lazy(() => import("./pages/RateCalculator"));
const RateCard = lazy(() => import("./pages/RateCard"));
const SupportWeb = lazy(() => import("./pages/SupportWeb"));
const Ticket = lazy(() => import("./pages/Ticket"));
const Returns = lazy(() => import("./pages/Returns"));
const AddReturns = lazy(() => import("./components/features/returns/AddReturns"));
const Order = lazy(() => import("./pages/Order"));
const BookShipment = lazy(() => import("./pages/BookShipment"));
const AddOrder = lazy(() => import("./components/features/orders/AddOrder"));
const Settings = lazy(() => import("./components/features/settings/Settings"));
const ManagePickupAddress = lazy(() => import("./components/features/settings/ManagePickupAddress"));
const CompanyProfile = lazy(() => import("./components/features/settings/CompanyProfile"));
const Kyc = lazy(() => import("./components/features/settings/Kyc"));
const OrderDetails = lazy(() => import("./components/features/orders/OrderDetails"));
const ReturnsDetail = lazy(() => import("./components/features/returns/ReturnsDetail"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ChangePassword = lazy(() => import("./components/features/settings/ChangePassword"));
const Customer = lazy(() => import("./components/features/setUp/Customer"));
const ForgotPassword = lazy(() => import("./components/features/auth/ForgotPassword"));
const Services = lazy(() => import("./components/features/setUp/Services"));
const CustomerDetails = lazy(() => import("./components/features/setUp/CustomerDetails"));
const TrackShipment = lazy(() => import("./pages/TrackShipment"));
const ShipmentProfiles = lazy(() => import("./components/features/setUp/ShipmentProfiles"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const BillingAddress = lazy(() => import("./pages/BillingAddress"));
const GstInvoice = lazy(() => import("./pages/GstInvoice"));
const Reports = lazy(() => import("./components/features/tools/Reports"));
const BulkPrintActions = lazy(() => import("./components/features/tools/BulkPrintActions"));
const Billing = lazy(() => import("./pages/Billing"));
const UserRoleManagement = lazy(() => import("./components/features/settings/UserRoleManagement"));
const UserRoleEdit = lazy(() => import("./components/features/settings/UserRoleEdit"));
const UserRoleAdd = lazy(() => import("./components/features/settings/UserRoleAdd"));
const TrackShipmentAwb = lazy(() => import("./pages/TrackShipmentAwb"));
const TrackShipmentOrder = lazy(() => import("./pages/TrackShipmentOrder"));
const TrackShipmentLr = lazy(() => import("./pages/TrackShipmentLr"));
const TrackTicketStatus = lazy(() => import("./pages/TrackTicketStatus"));
const MISReports = lazy(() => import("./components/features/tools/MISReports"));
const SalesChannels = lazy(() => import("./components/features/channels/SalesChannels"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // staleTime: 60 * 1000,
      staleTime: 0,
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => {
  return (
    <>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <DrawerProvider />
          <ModalProvider />
          <Suspense fallback={<LinearProgress sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 2000, height: 3 }} />}>
          <Routes>
            <Route path="tracking-shipment-order/:order_id" element={<TrackShipmentOrder />} />
            <Route path="tracking-lr-order/:order_id" element={<TrackShipmentLr />} />
            <Route path="track-ticket" element={<TrackTicketStatus />} />
            <Route
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate replace to="dashboard" />} />
              <Route path="home" element={<Home />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="tracking/:order_id" element={<TrackShipment />} />
              <Route path="tracking-shipment/:order_id" element={<TrackShipmentAwb />} />
              <Route path="tracking-shipment-order/:order_id" element={<TrackShipmentOrder />} />

              <Route path="returns" element={<CommonRoute />}>
                <Route path="" element={<Returns />} />
                <Route path="add-returns" element={<AddReturns />} />
                <Route path="edit-returns" element={<AddReturns />} />
                <Route path="details/:slug" element={<ReturnsDetail />} />
              </Route>
              <Route path="order" element={<CommonRoute />}>
                <Route path="" element={<Order />} />
                <Route path="add-order" element={<AddOrder />} />
                <Route path="details/:order_id" element={<OrderDetails />} />
              </Route>
              <Route path="book_shipment" element={<CommonRoute />}>
                <Route path="" element={<BookShipment />} />
                <Route path="add-order" element={<AddOrder />} />
                <Route path="details/:order_id" element={<OrderDetails />} />
              </Route>
              <Route path="tools" element={<CommonRoute />}>
                <Route path="reports" element={<Reports />} />
                <Route path="mis-report" element={<MISReports />} />
                <Route path="bulk-action" element={<BulkPrintActions />} />
              </Route>
              <Route path="set-up-and-management" element={<CommonRoute />}>
                <Route path="customer_search" element={<Customer />} />
                <Route
                  path="shipment_profiles"
                  element={<ShipmentProfiles />}
                />
                <Route
                  path="customer_search/:mobileNumber"
                  element={<CustomerDetails />}

                  
                />
                <Route path="all_service" element={<Services />} />
              </Route>
              <Route path="support-and-help" element={<CommonRoute />}>
                <Route path="" element={<SupportWeb />} />
                <Route path="ticket" element={<Ticket />} />
              </Route>

              <Route path="weight-and-managements" element={<CommonRoute />}>
                <Route
                  path="weight-and-discrepancies"
                  element={<WeightDiscrepancies />}
                />
              </Route>
              <Route
                path="tools/rate-calculator"
                element={<RateCalculator />}
              />
              <Route path="tools/rate-card" element={<RateCard />} />
              <Route path="settings" element={<CommonRoute />}>
                <Route path="" element={<Settings />} />
                <Route path="change-password" element={<ChangePassword />} />

                <Route path="kyc" element={<Kyc />} />
                <Route
                  path="manage-pickup-addresses"
                  element={<ManagePickupAddress />}
                />
                <Route path="company-profile" element={<CompanyProfile />} />
                <Route path="sales-channels" element={<SalesChannels />} />
                <Route path="billing_invoicing" element={<BillingAddress />} />
                <Route path="gstin_invoicing" element={<GstInvoice />} />
                <Route
                  path="user-role-management"
                  element={<UserRoleManagement />}
                />
                <Route
                  path="user-role-management/add"
                  element={<UserRoleAdd />}
                />
                <Route
                  path="user-role-management/:user_id"
                  element={<UserRoleEdit />}
                />
                {/* <Route path="returns" element={<CommonRoute />}>
                <Route path="" element={<RefundSettings />} />
              </Route> */}
              </Route>
              <Route path="billing" element={<Billing />} />
            </Route>
            <Route path="login" element={<Login />} />
            <Route path="register" element={<Register />} />
            <Route path="forgot-password" element={<ForgotPassword />} />

            <Route path="hipship" element={<LandingPage />} />
            <Route path="*" element={<PageNotFound />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster
          position="bottom-right"
          gutter={12}
          containerStyle={{ margin: "8px" }}
          toastOptions={{
            success: {
              duration: 2000,
            },
            error: {
              duration: 4500,
            },
            style: {
              fontSize: "14px",
              maxWidth: "440px",
              padding: "10px 16px",
              borderRadius: "10px",
              backgroundColor: "#27374d",
              color: "#fff",
            },
          }}
        />
      </QueryClientProvider>
    </>
  );
};

export default App;
